import { useMutation, useQuery } from "@apollo/client/react";
import { captureException } from "@sentry/nextjs";
import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useEngagementTracking } from "../../hooks/useEngagementTracking";

import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import {
  CreateCustomerHostedResultDocument,
  GetViewerForHostedResultDocument,
  UpdateCustomerHostedResultDocument,
} from "../results/generated";
import { ContactStep, PhoneStep } from "../results/hostedSteps";
import { FunnelButton } from "../FunnelButton";
import { useFormFunnelTracking } from "../hooks/useFormFunnelTracking";
import type { CustomerDataBuilder } from "../results/hostedSteps";
import {
  getHostedProgressPosition,
  type FunnelProgressUpdate,
  type PhoneProgressStage,
  type ProgressDirection,
} from "../utils/funnelProgress";

export interface PreMatchContactPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface PreMatchHostedFlowProps {
  funnelId?: string;
  formFunnelSessionId?: string;
  isPreview?: boolean;
  /**
   * Called once first/last/email/phone are all collected and the phone is
   * verified. Parent should reissue `getFormFunnelMatches` with this payload
   * threaded into `contactData` so the pre-ping loop fires with real identity
   * fields. ENG-6248.
   */
  onComplete: (payload: PreMatchContactPayload) => void;
  onProgressChange?: (progress: FunnelProgressUpdate) => void;
}

/**
 * Reuses the hosted-flow ContactStep + PhoneStep to collect first/last/email
 * (creating the User on submit) and a verified phone *before* the pre-ping
 * loop runs. Lives between the "Finding your match…" loading screen and
 * "Verifying your match…" — so users perceive it as confirming a match, not a
 * gatekeeping question.
 *
 * Returning-user short-circuit: if the viewer already has nameFirst+email,
 * skip ContactStep. If their phone is verified, skip PhoneStep too — when
 * everything is present we immediately invoke `onComplete` from the viewer
 * data and never render anything.
 */
export const PreMatchHostedFlow = ({
  onComplete,
  funnelId,
  formFunnelSessionId,
  isPreview,
  onProgressChange,
}: PreMatchHostedFlowProps) => {
  const { loggedIn, customerId } = useAuthenticatedContext();
  const { trackHostedFlowStep } = useFormFunnelTracking();

  const {
    data: viewerData,
    refetch: refetchViewer,
    loading: viewerLoading,
    error: viewerError,
  } = useQuery(GetViewerForHostedResultDocument, { skip: !loggedIn });
  const viewerCustomer = viewerData?.viewerCustomer;
  // Apollo can retain the previous viewer while a newly signed-in account loads.
  // Never use that previous account's identity to complete the new account's flow.
  const hasStaleViewer = !!(
    loggedIn &&
    customerId &&
    viewerCustomer?.id &&
    customerId !== viewerCustomer.id
  );
  const user = loggedIn && !hasStaleViewer ? viewerCustomer?.user || null : null;
  const identity = loggedIn ? customerId || viewerCustomer?.id || user?.id || null : null;

  const [updateCustomer] = useMutation(UpdateCustomerHostedResultDocument);
  const [createCustomer] = useMutation(CreateCustomerHostedResultDocument);

  const customerDataRef = useRef<CustomerDataBuilder>({
    nameFirst: "",
    nameLast: "",
    email: "",
    phone: "",
  });

  // Entry identity decides which screens can be skipped. An anonymous visitor
  // creating their first account stays in the active contact action; switching
  // an existing account starts fresh and invalidates that account's callbacks.
  const identityRef = useRef(identity);
  const progressStepsRef = useRef<readonly string[] | null>(null);
  const completedRef = useRef(false);
  const identityLoadRequestRef = useRef(0);
  const [identityLoadState, setIdentityLoadState] = useState<"idle" | "loading" | "error">(
    "idle"
  );
  const [navigation, setNavigation] = useState({
    stepIndex: 0 as 0 | 1,
    phoneStage: "phone" as PhoneProgressStage,
    generation: 0,
    direction: "forward" as ProgressDirection,
  });
  const navigationRef = useRef(navigation);
  navigationRef.current = navigation;
  if (identityRef.current !== identity && (identity !== null || !loggedIn)) {
    const previousIdentity = identityRef.current;
    identityRef.current = identity;
    if (previousIdentity !== null) {
      identityLoadRequestRef.current += 1;
      setIdentityLoadState("idle");
      customerDataRef.current = { nameFirst: "", nameLast: "", email: "", phone: "" };
      progressStepsRef.current = null;
      completedRef.current = false;
      const next = {
        stepIndex: 0,
        phoneStage: "phone",
        generation: navigation.generation + 1,
        direction: "forward",
      } as const;
      navigationRef.current = next;
      setNavigation(next);
    }
  }
  const { stepIndex, generation } = navigation;

  const retryEntryIdentity = useCallback(async () => {
    const request = ++identityLoadRequestRef.current;
    setIdentityLoadState("loading");
    try {
      await refetchViewer();
      if (request === identityLoadRequestRef.current) setIdentityLoadState("idle");
    } catch (error) {
      if (request !== identityLoadRequestRef.current) return;
      captureException(error);
      setIdentityLoadState("error");
    }
  }, [refetchViewer]);

  useEffect(() => {
    // The old phone screen may unmount before its account-change effect runs.
    if (hasStaleViewer) void retryEntryIdentity();
  }, [hasStaleViewer, customerId, retryEntryIdentity]);

  // Accept viewer fields after identity reconciliation. Same-account refetches
  // preserve the values collected by an action that is still finishing.
  useEffect(() => {
    if (!user) return;
    if (user.nameFirst && !customerDataRef.current.nameFirst) {
      customerDataRef.current.nameFirst = user.nameFirst;
    }
    if (user.nameLast && !customerDataRef.current.nameLast) {
      customerDataRef.current.nameLast = user.nameLast;
    }
    if (user.email && !customerDataRef.current.email) {
      customerDataRef.current.email = user.email;
    }
    if (user.phoneVerified && user.phone && !customerDataRef.current.phone) {
      customerDataRef.current.phone = user.phone;
    }
  }, [user]);

  // A missing last name must still be collected before pre-pinging a school.
  const hasContact = !!(user?.nameFirst && user?.nameLast && user?.email);
  const hasVerifiedPhone = !!(user?.phoneVerified && user.phone);
  if (
    progressStepsRef.current === null &&
    (!loggedIn || (!viewerLoading && !hasStaleViewer && viewerData !== undefined))
  ) {
    progressStepsRef.current = [
      ...(!hasContact ? ["contact"] : []),
      ...(!hasVerifiedPhone ? ["phone"] : []),
    ];
  }
  const resolvingEntryIdentity = progressStepsRef.current === null;

  useEffect(() => {
    // Fields learned during the active contact action advance through its
    // successful callback. Only contact known at entry skips the screen.
    if (!hasContact || progressStepsRef.current?.includes("contact")) return;
    setNavigation((current) =>
      current.stepIndex === 1
        ? current
        : {
            stepIndex: 1,
            phoneStage: "phone",
            generation: current.generation + 1,
            direction: "forward",
          }
    );
  }, [hasContact, identity]);

  // Full short-circuit: viewer already has all four fields. Fire onComplete
  // exactly once. We don't call setState here so we don't render anything.
  useEffect(() => {
    if (completedRef.current) return;
    if (!hasContact || !hasVerifiedPhone || progressStepsRef.current?.length !== 0) return;
    if (!user?.nameFirst || !user.nameLast || !user.email || !user.phone) return;
    completedRef.current = true;
    onComplete({
      firstName: user.nameFirst,
      lastName: user.nameLast,
      email: user.email,
      phone: user.phone,
    });
  }, [hasContact, hasVerifiedPhone, user, onComplete]);

  const handlePhoneProgressStageChange = useCallback(
    (stage: PhoneProgressStage) => {
      setNavigation((current) => {
        if (
          current.generation !== generation ||
          current.stepIndex !== 1 ||
          current.phoneStage === stage
        ) {
          return current;
        }
        return { ...current, phoneStage: stage, direction: "forward" };
      });
    },
    [generation]
  );

  const progressSteps = progressStepsRef.current;
  useEffect(() => {
    if (
      !progressSteps ||
      resolvingEntryIdentity ||
      completedRef.current ||
      (hasContact && stepIndex === 0)
    ) {
      return;
    }
    const progressStepIndex = progressSteps.indexOf(stepIndex === 0 ? "contact" : "phone");
    if (progressStepIndex < 0) return;
    onProgressChange?.({
      phase: "preMatchHosted",
      ...getHostedProgressPosition(
        progressSteps,
        progressStepIndex,
        hasVerifiedPhone ? "code" : navigation.phoneStage
      ),
      direction: navigation.direction,
    });
  }, [
    progressSteps,
    resolvingEntryIdentity,
    hasContact,
    hasVerifiedPhone,
    stepIndex,
    navigation.phoneStage,
    navigation.direction,
    onProgressChange,
  ]);

  // Resolve a returning account before its first screen. Later authentication
  // and same-account refetches must keep an in-flight action mounted.
  useEngagementTracking(
    !isPreview &&
      !resolvingEntryIdentity &&
      !completedRef.current &&
      !(hasContact && hasVerifiedPhone)
      ? {
          scope: "step",
          phase: "pre_match_hosted",
          stepId: stepIndex === 0 ? "contact" : "phone",
          stepIndex,
          formFunnelId: funnelId,
          formFunnelSessionId,
        }
      : null
  );
  if (resolvingEntryIdentity) {
    if (identityLoadState === "error" || (viewerError && identityLoadState !== "loading")) {
      return (
        <Box sx={{ py: 6 }}>
          <Typography role="alert" sx={{ mb: 2 }}>
            We could not load your account. Please try again.
          </Typography>
          <FunnelButton onClick={() => void retryEntryIdentity()}>Try again</FunnelButton>
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (completedRef.current) {
    // Avoids a one-frame flash of the contact form after we fire onComplete.
    return null;
  }

  if (stepIndex === 0) {
    return (
      <ContactStep
        key={generation}
        user={user}
        loggedIn={loggedIn}
        viewerData={viewerData}
        customerDataRef={customerDataRef}
        updateCustomer={updateCustomer}
        createCustomer={createCustomer}
        refetchViewer={refetchViewer}
        onNext={() => {
          const current = navigationRef.current;
          if (current.generation !== generation || current.stepIndex !== 0) return;
          if (completedRef.current) return;
          if (!isPreview) trackHostedFlowStep("contact", "pre_match");
          if (!progressStepsRef.current?.includes("phone")) {
            completedRef.current = true;
            onComplete({
              firstName: customerDataRef.current.nameFirst,
              lastName: customerDataRef.current.nameLast,
              email: customerDataRef.current.email,
              phone: customerDataRef.current.phone,
            });
            return;
          }
          const next = {
            stepIndex: 1,
            phoneStage: "phone",
            generation: current.generation + 1,
            direction: "forward",
          } as const;
          navigationRef.current = next;
          setNavigation(next);
        }}
      />
    );
  }

  return (
    <PhoneStep
      key={generation}
      user={user}
      customerDataRef={customerDataRef}
      refetchViewer={refetchViewer}
      onProgressStageChange={handlePhoneProgressStageChange}
      onNext={() => {
        if (navigationRef.current.generation !== generation) return;
        if (completedRef.current) return;
        completedRef.current = true;
        if (!isPreview) trackHostedFlowStep("phone", "pre_match");
        onComplete({
          firstName: customerDataRef.current.nameFirst,
          lastName: customerDataRef.current.nameLast,
          email: customerDataRef.current.email,
          phone: customerDataRef.current.phone,
        });
      }}
      onBack={
        hasContact
          ? undefined
          : () => {
              const current = navigationRef.current;
              const next = {
                stepIndex: 0,
                phoneStage: "phone",
                generation: current.generation + 1,
                direction: "back",
              } as const;
              navigationRef.current = next;
              setNavigation(next);
            }
      }
    />
  );
};
